import { useQuery } from "@tanstack/react-query"
import { Link, useNavigate, useSearchParams } from "react-router"

import { EmptyState, SkeletonText } from "@/components/ui/alert"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DETECTOR_SETTING_KEYS,
  detectorProfileOptionsQuery,
  settingOf,
  valueLabel,
  type DetectorProfileOptions,
} from "@/components/domain/detector-profile"
import { formatDateShort } from "@/lib/format-date"
import { cn } from "@/lib/utils"

import { evalRunsQuery } from "./api"
import { profileOrigin } from "./dataset-profile"
import { RunStatusBadge } from "./eval-run-manage"
import { meetsTarget, type EvalRun, type FigureFormat } from "./eval-runs-data"
import { hasScopeFigures, pairFigure, type FigurePair } from "./scope"
import { PairedFigure, ScopeSwitch } from "./scope-controls"
import { useExcludeOutOfScope } from "./use-scope-preference"

// The measurements, newest first: an admin-only development tool, never shown to clients.
// Archived runs are kept but left out of the list unless asked for; the choice lives in the URL so
// coming back from a run keeps it.

export function EvalRunsPage() {
  const runs = useQuery(evalRunsQuery())
  const options = useQuery(detectorProfileOptionsQuery())
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const showArchived = params.get("archived") === "1"
  const shown = runs.data?.filter((r) => showArchived || r.status !== "archived") ?? []
  const archivedCount = runs.data?.filter((r) => r.status === "archived").length ?? 0
  const [preferExclude, setExclude] = useExcludeOutOfScope()
  const scoped = runs.data?.some((r) => hasScopeFigures(r.summary)) ?? false

  return (
    <div className="flex h-full flex-col gap-5 overflow-auto px-4 py-5 sm:px-6">
      <header className="flex flex-wrap items-end gap-x-4 gap-y-3">
        <div className="min-w-0 flex-1 basis-72">
          <h1 className="text-subtitle text-text">Evaluation runs</h1>
          <p className="mt-1 max-w-3xl text-body-sm text-text-2">
            The review measured against expert-marked labels. Admins only: a run moves from Draft to
            Reviewed to Archived and is never shown to clients.
          </p>
        </div>
        {runs.isSuccess && (
          <ScopeSwitch checked={preferExclude} onChange={setExclude} available={scoped} />
        )}
        {archivedCount > 0 && (
          <label className="flex items-center gap-2 text-body-sm text-text-2">
            <Checkbox
              checked={showArchived}
              onCheckedChange={(checked) => {
                setParams(
                  (prev) => {
                    const out = new URLSearchParams(prev)
                    if (checked === true) out.set("archived", "1")
                    else out.delete("archived")
                    return out
                  },
                  { replace: true },
                )
              }}
            />
            Show archived ({archivedCount})
          </label>
        )}
      </header>

      {runs.isError && (
        <EmptyState reason="error" title="The runs could not be loaded" body={runs.error.message} />
      )}
      {runs.isPending && <SkeletonText lines={3} />}
      {runs.isSuccess && runs.data.length === 0 && (
        <EmptyState
          title="No run yet"
          body={
            <>
              Runs arrive from the metrics tooling: score a run, then publish it with{" "}
              <code className="font-mono text-text">scripts/eval-run.sh publish</code> and an
              integration key. It lands here as a draft.
            </>
          }
          action={
            <Link
              to="/dev/integrations?tab=docs"
              className="text-body-sm font-medium text-brand hover:underline"
            >
              Integrations › API documentation
            </Link>
          }
        />
      )}
      {runs.isSuccess && runs.data.length > 0 && shown.length === 0 && (
        <EmptyState title="Every run is archived" body="Tick Show archived to see them." />
      )}
      {shown.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          <div className="overflow-x-auto">
            <table className="w-full text-body-sm">
              <thead className="bg-surface-sunken text-left text-caption font-medium text-text-2">
                <tr>
                  <th className="px-4 py-2">Run</th>
                  <th className="hidden px-3 py-2 sm:table-cell">Dataset</th>
                  <th className="hidden px-3 py-2 text-right md:table-cell">Labels</th>
                  <th className="px-3 py-2 text-right" title="Caught or escalated; target ≥ 90%">
                    Safety recall
                  </th>
                  <th
                    className="px-3 py-2 text-right"
                    title="needs_sme findings per commented label; target < 3.00"
                  >
                    Hand-offs
                  </th>
                  <th
                    className="hidden px-3 py-2 md:table-cell"
                    title="Where the run's detector settings came from"
                  >
                    Profile
                  </th>
                  <th className="hidden px-3 py-2 lg:table-cell">Measured</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((run) => {
                  const href = `/evaluations/${String(run.id)}`
                  // A run with no in-scope figures shows its all-marks ones either way.
                  const exclude = preferExclude && hasScopeFigures(run.summary)
                  const recall = pairFigure(run.summary, "safety_recall", exclude)
                  const handoffs = pairFigure(run.summary, "handoffs_per_commented_label", exclude)
                  return (
                    <tr
                      key={run.id}
                      className={cn(
                        "cursor-pointer border-t border-border-soft hover:bg-surface-sunken",
                        run.status === "archived" && "text-text-3",
                      )}
                      onClick={() => {
                        void navigate(href)
                      }}
                    >
                      <td className="px-4 py-2.5">
                        <Link
                          to={href}
                          className="font-medium text-text hover:text-brand hover:underline"
                          onClick={(e) => {
                            e.stopPropagation()
                          }}
                        >
                          {run.name}
                        </Link>
                        {(run.scopeDecisions ?? 0) > 0 && (
                          <p className="mt-0.5 text-caption text-text-3">
                            {publishedNote(run.scopeDecisions ?? 0)}
                          </p>
                        )}
                      </td>
                      <td className="hidden px-3 py-2.5 font-mono text-caption text-text-2 sm:table-cell">
                        {run.dataset}
                      </td>
                      <td className="hidden px-3 py-2.5 text-right tabular-nums md:table-cell">
                        {run.labelCount.toLocaleString()}
                      </td>
                      <Figure
                        pair={recall}
                        format="percent"
                        ok={meetsTarget("safety_recall", recall.primary)}
                      />
                      <Figure
                        pair={handoffs}
                        format="decimal"
                        ok={meetsTarget("handoffs_per_commented_label", handoffs.primary)}
                      />
                      <td
                        className="hidden px-3 py-2.5 whitespace-nowrap text-text-2 md:table-cell"
                        title={profileTitle(run, options.data)}
                      >
                        {profileOrigin(run.detectorProfile, run.datasetProfile)}
                      </td>
                      <td className="hidden px-3 py-2.5 whitespace-nowrap text-text-2 lg:table-cell">
                        {formatDateShort(run.createdAt)}
                      </td>
                      <td className="px-4 py-2.5">
                        <RunStatusBadge status={run.status} />
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

function Figure({
  pair,
  format,
  ok,
}: {
  pair: FigurePair
  format: FigureFormat
  ok: boolean | null
}) {
  return (
    <td
      className={cn(
        "px-3 py-2.5 text-right font-medium whitespace-nowrap tabular-nums",
        ok === true && "text-master",
        ok === false && "text-err",
      )}
    >
      <PairedFigure pair={pair} format={format} />
      {ok !== null && <span className="sr-only">{ok ? " meets target" : " below target"}</span>}
    </td>
  )
}

/**
 * The list reads each run's summary as published, and the count it carries is the dataset's, some
 * of which may name marks this run does not carry. So it says the figures may change, not that
 * they did: only the run itself knows.
 */
function publishedNote(decisions: number): string {
  const n = `${String(decisions)} admin scope ${decisions === 1 ? "decision" : "decisions"}`
  return `Figures as published. ${n} on this dataset may change the in-scope ones: open the run.`
}

/** Every setting the run applied, one per line, for the Profile cell's tooltip. */
function profileTitle(
  run: EvalRun,
  options: DetectorProfileOptions | undefined,
): string | undefined {
  const resolved = run.detectorProfile
  if (!resolved || !options) return undefined
  return DETECTOR_SETTING_KEYS.map((key) => {
    const value = resolved.values[key]
    const label = settingOf(options, key)?.label ?? key
    return `${label}: ${value === undefined ? "Not recorded" : valueLabel(options, key, value)}`
  }).join("\n")
}
